import { apiClient } from './apiClient';
import {
  User,
  UserCreate,
  UserUpdate,
  UserListResponse,
  UserGroup,
  PasswordReset,
  UserWithDetails,
  UserClientRoles
} from '../types/user';

class UserService {
  private usersCache: User[] = [];
  private lastFetch: number = 0;
  private cacheDuration: number = 30000; // 30 seconds

  /**
   * Fetch users with pagination and optional search
   */
  async fetchUsers(first: number = 0, max: number = 100, search?: string, includeInactive: boolean = false): Promise<UserListResponse> {
    try {
      let url = `/v2/users?first=${first}&max=${max}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (includeInactive) {
        url += `&include_inactive=true`;
      }

      const response = await apiClient.get(url);

      // Update cache
      if (first === 0 && !search && !includeInactive) {
        this.usersCache = response.users || response;
        this.lastFetch = Date.now();
      }

      // Handle both array response and object response
      if (Array.isArray(response)) {
        return {
          users: response,
          total: response.length,
          first,
          max
        };
      }

      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to fetch users:', error);
      throw error;
    }
  }

  /**
   * Get a single user by ID
   */
  async getUser(userId: string): Promise<User> {
    try {
      const response = await apiClient.get(`/v2/users/${userId}`);
      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to get user:', error);
      throw error;
    }
  }

  /**
   * Get a user by username
   */
  async getUserByUsername(username: string): Promise<User> {
    try {
      const response = await apiClient.get(`/v2/users/username/${encodeURIComponent(username)}`);
      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to get user by username:', error);
      throw error;
    }
  }

  /**
   * Create a new user
   */
  async createUser(userData: UserCreate): Promise<User> {
    try {
      const response = await apiClient.post('/v2/users', userData);
      this.clearCache();
      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to create user:', error);
      throw error;
    }
  }

  /**
   * Update an existing user
   */
  async updateUser(userId: string, userData: UserUpdate): Promise<User> {
    try {
      const response = await apiClient.patch(`/v2/users/${userId}`, userData);
      this.clearCache();
      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to update user:', error);
      throw error;
    }
  }

  /**
   * Delete a user
   */
  /**
   * Suspend an account: it stays in Keycloak, keeps its username reserved,
   * and can be re-enabled. This is NOT a deletion.
   */
  async suspendUser(userId: string): Promise<void> {
    try {
      await apiClient.delete(`/v2/users/${userId}`);
      this.clearCache();
    } catch (error) {
      console.error('[USER SERVICE] Failed to suspend user:', error);
      throw error;
    }
  }

  /**
   * Permanently delete an account and free its username. Irreversible.
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      await apiClient.delete(`/v2/users/${userId}?hard=true`);
      this.clearCache();
    } catch (error) {
      console.error('[USER SERVICE] Failed to delete user:', error);
      throw error;
    }
  }

  /**
   * Reset a user's password
   */
  async resetPassword(userId: string, passwordData: PasswordReset): Promise<void> {
    try {
      await apiClient.post(`/v2/users/${userId}/reset-password`, passwordData);
    } catch (error) {
      console.error('[USER SERVICE] Failed to reset password:', error);
      throw error;
    }
  }

  /**
   * Get user's groups
   */
  async getUserGroups(userId: string): Promise<UserGroup[]> {
    try {
      const response = await apiClient.get(`/v2/users/${userId}/groups`);
      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to get user groups:', error);
      throw error;
    }
  }

  /**
   * Add user to a group
   */
  async addUserToGroup(userId: string, groupId: string): Promise<void> {
    try {
      await apiClient.put(`/v2/users/${userId}/groups/${groupId}`);
    } catch (error) {
      console.error('[USER SERVICE] Failed to add user to group:', error);
      throw error;
    }
  }

  /**
   * Remove user from a group
   */
  async removeUserFromGroup(userId: string, groupId: string): Promise<void> {
    try {
      await apiClient.delete(`/v2/users/${userId}/groups/${groupId}`);
    } catch (error) {
      console.error('[USER SERVICE] Failed to remove user from group:', error);
      throw error;
    }
  }

  /**
   * Get user's client roles
   */
  async getUserClientRoles(userId: string, clientId: string): Promise<UserClientRoles> {
    try {
      const response = await apiClient.get(`/v2/users/${userId}/client-roles?client_id=${encodeURIComponent(clientId)}`);
      return response;
    } catch (error) {
      console.error('[USER SERVICE] Failed to get user client roles:', error);
      throw error;
    }
  }

  /**
   * Add client role to user
   */
  async addClientRoleToUser(userId: string, clientId: string, roleName: string): Promise<void> {
    try {
      await apiClient.post(`/v2/users/${userId}/client-roles`, {
        client_id: clientId,
        role_name: roleName
      });
    } catch (error) {
      console.error('[USER SERVICE] Failed to add client role to user:', error);
      throw error;
    }
  }

  /**
   * Remove client role from user
   */
  async removeClientRoleFromUser(userId: string, clientId: string, roleName: string): Promise<void> {
    try {
      const url = `/v2/users/${userId}/client-roles?client_id=${encodeURIComponent(clientId)}&role_name=${encodeURIComponent(roleName)}`;
      await apiClient.delete(url);
    } catch (error) {
      console.error('[USER SERVICE] Failed to remove client role from user:', error);
      throw error;
    }
  }

  /**
   * Transform User to UserWithDetails for UI display
   */
  transformToUserWithDetails(user: User): UserWithDetails {
    return {
      ...user,
      fullName: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username,
      createdDate: user.createdTimestamp ? new Date(user.createdTimestamp) : undefined
    };
  }

  /**
   * Clear the users cache
   */
  clearCache(): void {
    this.usersCache = [];
    this.lastFetch = 0;
  }

  /**
   * Get cached users if available
   */
  getCachedUsers(): User[] {
    const now = Date.now();
    if (this.usersCache.length > 0 && now - this.lastFetch < this.cacheDuration) {
      return this.usersCache;
    }
    return [];
  }
}

export const userService = new UserService();
